import {
  formatNdtLaudo,
  normalizeCommon,
  validateCommon,
  type NdtDocumentCommon,
  type NdtLaudo,
  type NdtReportCommon,
  type NdtValidationResult
} from "./ndt-report-common.js";
import {
  US_JOINT_TYPE_IDS,
  USC_JOINT_DEFINITION_VERSION,
  inspectionPointsOf,
  isValidInspectionPoint,
  usJointById,
  usJointGeometryFingerprint,
  usJointLabel,
  usJointSvg,
  type UsJointTypeId
} from "./us-joint-types.js";

/**
 * Relatório de Ensaio por Ultrassom Convencional (USC) — FORM-USC REV.00.
 *
 * Ensaio de detecção/avaliação de descontinuidades. Domínio PRÓPRIO,
 * independente do ME (medição de espessura), do B-Scan, do PAUT e do TOFD.
 *
 * Seções do modelo: 01 Identificação · 02 Dados do Ensaio · 03 Aparelho,
 * Transdutores e Calibração · 04 Tipos de Juntas Ensaiadas · 05 Registro da
 * Inspeção / Resultados · 06 Croqui · 07 Observações e Parecer Final ·
 * 08 Evidências Fotográficas · 09 Termo de Responsabilidade & Signatários.
 */

export type UscTechnique = "PULSO_ECO" | "FEIXE_RETO" | "FEIXE_ANGULAR" | "OUTRA" | "";
export const USC_TECHNIQUE_OPTIONS: ReadonlyArray<{ value: UscTechnique; label: string }> = [
  { value: "", label: "Selecione" },
  { value: "PULSO_ECO", label: "Pulso-eco" },
  { value: "FEIXE_RETO", label: "Feixe reto" },
  { value: "FEIXE_ANGULAR", label: "Feixe angular" },
  { value: "OUTRA", label: "Outra" }
];

export function formatUscTechnique(v: UscTechnique | null | undefined, other = ""): string {
  if (!v) return "";
  if (v === "OUTRA") return other.trim() || "Outra";
  return USC_TECHNIQUE_OPTIONS.find(o => o.value === v)?.label ?? String(v);
}

/** Transdutor como o relatório o guarda (linha da tabela do item 03): vínculo + texto congelado. */
export interface UscTransducerRef {
  key: string;
  assetId: string | null;
  type: string;
  frequency: string;
  angle: string;
  dimension: string;
  serialNumber: string;
}

/**
 * Tipo de junta selecionado no relatório: id + versão da definição gráfica
 * + assinatura da geometria no momento da seleção. Um documento assinado
 * guarda a figura que usou, mesmo que o catálogo de modelos mude depois.
 */
export interface UscSelectedJointType {
  id: UsJointTypeId;
  code: string;
  definitionVersion: number;
  geometryFingerprint: string;
}

/** Linha de "05 REGISTRO DA INSPEÇÃO / RESULTADOS" do FORM-USC. */
export interface UscResultItem {
  key: string;
  item: string;
  /** JUNTA / REGIÃO */
  jointRegion: string;
  /** TIPO DE JUNTA — um dos tipos selecionados no item 04 (ou vazio). */
  jointTypeId: UsJointTypeId | "";
  /** PONTO DE INSPEÇÃO — só um ponto que exista na figura do tipo escolhido. */
  inspectionPoint: number | null;
  location: string;
  indication: string;
  sizing: string;
  laudo: NdtLaudo;
}

export interface ConventionalUsReport extends NdtReportCommon<ConventionalUsDocumentModel> {
  // 02 — DADOS DO ENSAIO
  surfaceCondition: string;
  technique: UscTechnique;
  techniqueOther: string;
  couplantId: string | null;
  couplant: string;

  // 03 — APARELHO, TRANSDUTORES E CALIBRAÇÃO
  deviceAssetId: string | null;
  deviceManufacturer: string;
  deviceModel: string;
  deviceSerialNumber: string;
  deviceCalibrationCert: string;
  deviceCalibrationDate: string;
  deviceCalibrationValidity: string;
  calibrationBlockAssetId: string | null;
  calibrationBlock: string;
  calibrationBlockType: string;
  calibrationBlockCert: string;
  calibrationBlockCertValidity: string;
  verificationReference: string;
  calibrationNote: string;
  transducers: UscTransducerRef[];

  // 04 — TIPOS DE JUNTAS ENSAIADAS (seleção múltipla: um ensaio pode ter registros de mais de um tipo)
  jointTypes: UscSelectedJointType[];

  // 05 — REGISTRO DA INSPEÇÃO / RESULTADOS
  results: UscResultItem[];
}

export interface ConventionalUsDocumentModel extends NdtDocumentCommon {
  testData: {
    procedure: string;
    referenceNorm: string;
    acceptanceCriteria: string;
    baseMaterial: string;
    surfaceCondition: string;
    testTemperature: string;
    technique: string;
    couplant: string;
  };
  equipment: {
    device: string;
    deviceCertificate: string;
    calibrationBlock: string;
    calibrationBlockCertificate: string;
    verificationReference: string;
    note: string;
  };
  transducers: Array<{ number: string; type: string; frequency: string; angle: string; dimension: string; serialNumber: string }>;
  /**
   * Os QUATRO modelos, sempre (o FORM-USC imprime a matriz completa), cada
   * um com `selected` e o SVG da geometria — a mesma da página de elaboração.
   */
  jointTypes: Array<{ id: UsJointTypeId; code: string; selected: boolean; svg: string; inspectionPoints: number[]; definitionVersion: number }>;
  results: Array<{ item: string; jointRegion: string; jointType: string; inspectionPoint: string; location: string; indication: string; sizing: string; laudo: NdtLaudo; laudoLabel: string }>;
}

/** Marca/desmarca um tipo — a seleção grava a versão e a assinatura da geometria do momento. */
export function toggleJointType(current: readonly UscSelectedJointType[], id: UsJointTypeId, selected: boolean): UscSelectedJointType[] {
  const without = current.filter(j => j.id !== id);
  if (!selected) return without;
  const model = usJointById(id);
  if (!model) return [...without];
  const next = [...without, { id, code: model.code, definitionVersion: model.version, geometryFingerprint: usJointGeometryFingerprint(id) }];
  return next.sort((a, b) => US_JOINT_TYPE_IDS.indexOf(a.id) - US_JOINT_TYPE_IDS.indexOf(b.id));
}

export function isJointTypeSelected(report: Pick<ConventionalUsReport, "jointTypes">, id: string): boolean {
  return report.jointTypes.some(j => j.id === id);
}

/** Pontos disponíveis para uma linha de resultado: os da figura do tipo escolhido — nunca uma lista manual. */
export function availablePointsForRow(jointTypeId: string | null | undefined): readonly number[] {
  return inspectionPointsOf(jointTypeId);
}

export function validateConventionalUsReportCompleteness(report: ConventionalUsReport): NdtValidationResult {
  const errors = validateCommon(report, "US");
  if (!report.technique) errors.push("Selecione a Técnica.");
  if (!report.couplantId && !report.couplant.trim()) errors.push("Selecione o Acoplante.");
  if (!report.deviceAssetId && !report.deviceManufacturer.trim()) errors.push("Selecione o Aparelho de Ultrassom.");
  if (!report.calibrationBlockAssetId && !report.calibrationBlock.trim()) errors.push("Selecione o Bloco de Calibração.");
  if (report.transducers.length === 0 || report.transducers.every(t => !t.assetId && !t.type.trim())) errors.push("Selecione ao menos um Transdutor.");
  if (report.jointTypes.length === 0) errors.push("Selecione ao menos um Tipo de Junta Ensaiada (item 04).");
  if (report.results.length === 0) {
    errors.push("Adicione ao menos um item no Registro da Inspeção.");
  } else {
    const withoutRegion = report.results.filter(r => !r.jointRegion.trim()).length;
    if (withoutRegion > 0) errors.push(`Existem ${withoutRegion} ${withoutRegion === 1 ? "item" : "itens"} sem Junta / Região informada.`);
    const withoutLaudo = report.results.filter(r => !r.laudo).length;
    if (withoutLaudo > 0) errors.push(`Existem ${withoutLaudo} ${withoutLaudo === 1 ? "item" : "itens"} sem Laudo.`);
    for (const row of report.results) {
      if (row.jointTypeId && !isJointTypeSelected(report, row.jointTypeId)) {
        errors.push(`Item ${row.item}: o ${usJointLabel(row.jointTypeId)} não está entre os Tipos de Juntas Ensaiadas selecionados no item 04.`);
      }
      if (row.inspectionPoint !== null && !isValidInspectionPoint(row.jointTypeId, row.inspectionPoint)) {
        errors.push(`Item ${row.item}: o Ponto de Inspeção ${row.inspectionPoint} não existe na figura do ${usJointLabel(row.jointTypeId) || "tipo de junta"}.`);
      }
    }
  }
  return { valid: errors.length === 0, errors };
}

export function normalizeConventionalUsReport(report: ConventionalUsReport): ConventionalUsReport {
  const r = report as any;
  normalizeCommon(r, "USC");
  for (const key of ["results", "transducers", "jointTypes"]) if (!Array.isArray(r[key])) r[key] = [];
  for (const key of ["surfaceCondition", "techniqueOther", "couplant", "deviceManufacturer", "deviceModel", "deviceSerialNumber", "deviceCalibrationCert", "deviceCalibrationDate", "deviceCalibrationValidity", "calibrationBlock", "calibrationBlockType", "calibrationBlockCert", "calibrationBlockCertValidity", "verificationReference", "calibrationNote"]) {
    if (typeof r[key] !== "string") r[key] = "";
  }
  if (typeof r.technique !== "string") r.technique = "";
  if (r.couplantId === undefined) r.couplantId = null;
  if (r.deviceAssetId === undefined) r.deviceAssetId = null;
  if (r.calibrationBlockAssetId === undefined) r.calibrationBlockAssetId = null;
  r.transducers = r.transducers.map((raw: any, index: number) => ({
    key: raw.key || `USC-TR-LEGACY-${index}`,
    assetId: raw.assetId ?? null,
    type: raw.type ?? "",
    frequency: raw.frequency ?? "",
    angle: raw.angle ?? "",
    dimension: raw.dimension ?? "",
    serialNumber: raw.serialNumber ?? ""
  }));
  r.jointTypes = r.jointTypes
    .filter((j: any) => j && US_JOINT_TYPE_IDS.includes(j.id))
    .map((j: any) => ({ id: j.id, code: j.code || usJointLabel(j.id), definitionVersion: Number(j.definitionVersion ?? USC_JOINT_DEFINITION_VERSION), geometryFingerprint: j.geometryFingerprint || usJointGeometryFingerprint(j.id) }));
  r.results = r.results.map((raw: any, index: number) => ({
    key: raw.key || `USC-ITEM-LEGACY-${index}`,
    item: raw.item ?? String(index + 1).padStart(2, "0"),
    jointRegion: raw.jointRegion ?? "",
    jointTypeId: US_JOINT_TYPE_IDS.includes(raw.jointTypeId) ? raw.jointTypeId : "",
    inspectionPoint: raw.inspectionPoint === null || raw.inspectionPoint === undefined || raw.inspectionPoint === "" ? null : Number(raw.inspectionPoint),
    location: raw.location ?? "",
    indication: raw.indication ?? "",
    sizing: raw.sizing ?? "",
    laudo: raw.laudo ?? ""
  }));
  return report;
}

/** Os quatro modelos para o documento, com a marcação do que foi usado — SVG idêntico ao da página. */
export function jointTypesForDocument(report: Pick<ConventionalUsReport, "jointTypes">): ConventionalUsDocumentModel["jointTypes"] {
  return US_JOINT_TYPE_IDS.map(id => {
    const model = usJointById(id)!;
    const selected = report.jointTypes.find(j => j.id === id);
    return {
      id,
      code: model.code,
      selected: !!selected,
      svg: usJointSvg(id, { stroke: "#000000", strokeWidth: 2, width: model.diagram.width }),
      inspectionPoints: [...model.inspectionPoints],
      definitionVersion: selected?.definitionVersion ?? model.version
    };
  });
}

export { formatNdtLaudo as formatUscLaudo };
